import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CursorArrowRippleIcon,
  UserPlusIcon,
  CurrencyDollarIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ArrowPathIcon,
  TicketIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import AdminLayout from '../components/AdminLayout';
import LoadingSpinner from '../components/LoadingSpinner';
import StatsCard from '../components/StatsCard';
import Table from '../components/Table';
import Pagination from '../components/Pagination';
import useApi from '../hooks/useApi';
import toast from 'react-hot-toast';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

const CLIENT_URL = process.env.REACT_APP_CLIENT_URL || 'http://localhost:3000';

function AffiliateDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('clicks');
  const [clicksPage, setClicksPage] = useState(1);
  const [conversionsPage, setConversionsPage] = useState(1);
  const [commissionsPage, setCommissionsPage] = useState(1);
  const [copied, setCopied] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showDiscountModal, setShowDiscountModal] = useState(false);

  // Fetch affiliate details
  const { data, loading, error, refetch } = useApi(`/api/admin/affiliates/${id}`, [id, refreshKey]);
  const affiliate = data?.data;

  // Fetch clicks
  const { data: clicksData, loading: clicksLoading } = useApi(
    `/api/admin/affiliates/${id}/clicks?page=${clicksPage}&limit=20`,
    [id, clicksPage, activeTab === 'clicks', refreshKey]
  );

  // Fetch conversions
  const { data: conversionsData, loading: conversionsLoading } = useApi(
    `/api/admin/affiliates/${id}/conversions?page=${conversionsPage}&limit=20`,
    [id, conversionsPage, activeTab === 'conversions', refreshKey]
  );

  // Fetch commissions
  const { data: commissionsData, loading: commissionsLoading } = useApi(
    `/api/admin/affiliates/${id}/commissions?page=${commissionsPage}&limit=20`,
    [id, commissionsPage, activeTab === 'commissions', refreshKey]
  );

  const copyAffiliateLink = () => {
    const link = `${CLIENT_URL}?ref=${affiliate.code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Affiliate link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="large" />
        </div>
      </AdminLayout>
    );
  }

  if (error || !affiliate) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-red-600">Error loading affiliate details</p>
          <button
            onClick={() => navigate('/affiliates')}
            className="mt-4 text-purple-600 hover:text-purple-700"
          >
            Back to Affiliates
          </button>
        </div>
      </AdminLayout>
    );
  }

  const tabs = [
    { id: 'clicks', label: 'Clicks', count: affiliate.stats?.totalClicks || 0 },
    { id: 'conversions', label: 'Conversions', count: affiliate.stats?.totalConversions || 0 },
    { id: 'commissions', label: 'Commissions', count: (affiliate.stats?.pendingCommissions?.count || 0) + (affiliate.stats?.approvedCommissions?.count || 0) + (affiliate.stats?.paidCommissions?.count || 0) }
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <button
            onClick={() => navigate('/affiliates')}
            className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Affiliates
          </button>

          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{affiliate.name}</h1>
              <div className="mt-2 flex items-center space-x-4">
                <span className="text-gray-600">{affiliate.email}</span>
                {affiliate.phone && (
                  <span className="text-gray-600">• {affiliate.phone}</span>
                )}
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                  affiliate.is_active
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {affiliate.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            <button
              onClick={() => setRefreshKey(prev => prev + 1)}
              className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              title="Refresh data"
            >
              <ArrowPathIcon className="h-5 w-5 mr-2" />
              Refresh
            </button>
          </div>
        </div>

        {/* Affiliate Info Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Affiliate Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Affiliate Code</label>
              <div className="flex items-center space-x-2">
                <code className="text-lg font-mono font-bold text-purple-600">{affiliate.code}</code>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Affiliate Link</label>
              <div className="flex items-center space-x-2">
                <code className="text-sm text-gray-700 bg-gray-50 px-3 py-1 rounded flex-1 truncate">
                  {CLIENT_URL}?ref={affiliate.code}
                </code>
                <button
                  onClick={copyAffiliateLink}
                  className="p-2 text-gray-600 hover:text-purple-600 transition-colors"
                  title="Copy link"
                >
                  {copied ? (
                    <CheckIcon className="h-5 w-5 text-green-600" />
                  ) : (
                    <ClipboardDocumentIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {affiliate.notes && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-500 mb-1">Notes</label>
                <p className="text-gray-700">{affiliate.notes}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Created</label>
              <p className="text-gray-700">{new Date(affiliate.created_at).toLocaleDateString()}</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Last Updated</label>
              <p className="text-gray-700">{new Date(affiliate.updated_at).toLocaleDateString()}</p>
            </div>
          </div>
        </div>

        {/* Discount Coupon Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center">
              <TicketIcon className="h-6 w-6 text-purple-600 mr-2" />
              <h3 className="text-lg font-semibold text-gray-900">Discount Coupon Settings</h3>
            </div>
            <button
              onClick={() => setShowDiscountModal(true)}
              className="inline-flex items-center px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors text-sm font-medium"
            >
              <PencilIcon className="h-4 w-4 mr-1" />
              Edit
            </button>
          </div>
          
          {affiliate.discount_coupon ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">iCount Coupon Code</label>
                <code className="text-lg font-mono font-bold text-green-600">{affiliate.discount_coupon}</code>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Discount Value</label>
                <span className="text-lg font-semibold text-gray-900">
                  {affiliate.discount_type === 'fixed' 
                    ? (affiliate.discount_amount ? `$${parseFloat(affiliate.discount_amount).toFixed(2)}` : 'Not specified')
                    : (affiliate.discount_percent ? `${affiliate.discount_percent}%` : 'Not specified')
                  }
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Description</label>
                <span className="text-gray-700">
                  {affiliate.discount_description || 'No description'}
                </span>
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <TicketIcon className="h-12 w-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No discount coupon configured for this affiliate.</p>
              <p className="text-sm text-gray-400 mt-1">Click "Edit" to add a discount coupon that will be applied at checkout for customers referred by this affiliate.</p>
            </div>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <StatsCard
            icon={CursorArrowRippleIcon}
            title="Total Clicks"
            value={affiliate.stats?.totalClicks || 0}
            iconColor="text-blue-600"
            bgColor="bg-blue-50"
          />
          <StatsCard
            icon={UserPlusIcon}
            title="Conversions"
            value={affiliate.stats?.totalConversions || 0}
            iconColor="text-green-600"
            bgColor="bg-green-50"
            trendValue={`${affiliate.stats?.conversionRate || 0}% conversion rate`}
          />
          <StatsCard
            icon={CurrencyDollarIcon}
            title="Total Earnings"
            value={`$${affiliate.stats?.totalEarnings?.toFixed(2) || '0.00'}`}
            iconColor="text-purple-600"
            bgColor="bg-purple-50"
          />
          <StatsCard
            icon={CurrencyDollarIcon}
            title="Pending Commissions"
            value={`$${affiliate.stats?.pendingCommissions?.total?.toFixed(2) || '0.00'}`}
            iconColor="text-orange-600"
            bgColor="bg-orange-50"
            trendValue={`${affiliate.stats?.pendingCommissions?.count || 0} pending`}
          />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 py-4 px-1 text-center border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-purple-500 text-purple-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {tab.label}
                  <span className="ml-2 px-2 py-0.5 rounded-full bg-gray-100 text-xs">
                    {tab.count}
                  </span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'clicks' && (
              <ClicksTab
                data={clicksData?.data}
                loading={clicksLoading}
                page={clicksPage}
                onPageChange={setClicksPage}
              />
            )}
            {activeTab === 'conversions' && (
              <ConversionsTab
                data={conversionsData?.data}
                loading={conversionsLoading}
                page={conversionsPage}
                onPageChange={setConversionsPage}
              />
            )}
            {activeTab === 'commissions' && (
              <CommissionsTab
                data={commissionsData?.data}
                loading={commissionsLoading}
                page={commissionsPage}
                onPageChange={setCommissionsPage}
              />
            )}
          </div>
        </div>
      </div>

      {/* Discount Coupon Modal */}
      {showDiscountModal && (
        <DiscountCouponModal
          affiliate={affiliate}
          onClose={() => setShowDiscountModal(false)}
          onSuccess={() => {
            setShowDiscountModal(false);
            refetch();
            setRefreshKey(prev => prev + 1);
            toast.success('Discount settings updated successfully');
          }}
        />
      )}
    </AdminLayout>
  );
}

// Clicks Tab Component
function ClicksTab({ data, loading, page, onPageChange }) {
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  const clicks = data?.clicks || [];
  const totalPages = data?.totalPages || 1;

  if (clicks.length === 0) {
    return <div className="text-center text-gray-500 py-8">No clicks yet</div>;
  }

  const columns = [
    {
      key: 'clicked_at',
      label: 'Date',
      render: (click) => {
        const date = new Date(click.clicked_at);
        return (
          <div>
            <div className="font-medium">{date.toLocaleDateString()}</div>
            <div className="text-xs text-gray-500">{date.toLocaleTimeString()}</div>
          </div>
        );
      }
    },
    {
      key: 'visitor_id',
      label: 'Visitor ID',
      render: (click) => (
        <div className="font-mono text-xs">{click.visitor_id?.substring(0, 20)}...</div>
      )
    },
    {
      key: 'referrer',
      label: 'Referrer',
      render: (click) => {
        const referrer = click.referrer || 'Direct';
        try {
          const url = new URL(referrer);
          return (
            <div>
              <div className="font-medium">{url.hostname}</div>
              <div className="text-xs text-gray-500 truncate max-w-xs">{url.pathname}</div>
            </div>
          );
        } catch {
          return <span className="text-gray-500">{referrer}</span>;
        }
      }
    },
    {
      key: 'device',
      label: 'Device',
      render: (click) => {
        const ua = click.user_agent || '';
        const isMobile = /Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
        const isTablet = /iPad|Android(?!.*Mobile)/i.test(ua);
        
        if (isTablet) return '📱 Tablet';
        if (isMobile) return '📱 Mobile';
        return '💻 Desktop';
      }
    },
    {
      key: 'browser',
      label: 'Browser',
      render: (click) => {
        const ua = click.user_agent || '';
        // More comprehensive browser detection
        if (ua.includes('Edg')) return '🌐 Edge';
        if (ua.includes('Chrome') && !ua.includes('Edg')) return '🌐 Chrome';
        if (ua.includes('Safari') && !ua.includes('Chrome')) return '🌐 Safari';
        if (ua.includes('Firefox')) return '🌐 Firefox';
        if (ua.includes('Opera') || ua.includes('OPR')) return '🌐 Opera';
        if (ua.includes('Brave')) return '🌐 Brave';
        if (ua.includes('MSIE') || ua.includes('Trident')) return '🌐 IE';
        return ua ? '🌐 Other' : 'Unknown';
      }
    },
    {
      key: 'os',
      label: 'OS',
      render: (click) => {
        const ua = click.user_agent || '';
        if (ua.includes('Windows')) return '🖥️ Windows';
        if (ua.includes('Mac OS X') || ua.includes('Macintosh')) return '🍎 macOS';
        if (ua.includes('Linux')) return '🐧 Linux';
        if (ua.includes('Android')) return '🤖 Android';
        if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return '📱 iOS';
        return 'Unknown';
      }
    },
    {
      key: 'ip_address',
      label: 'IP Address',
      render: (click) => {
        const ip = click.ip_address;
        if (!ip) return 'N/A';
        // Handle IPv6 localhost
        if (ip === '::1' || ip === '::ffff:127.0.0.1') return '127.0.0.1 (localhost)';
        return <span className="font-mono text-xs">{ip}</span>;
      }
    },
    {
      key: 'expires_at',
      label: 'Attribution Expires',
      render: (click) => {
        const expires = new Date(click.expires_at);
        const now = new Date();
        const isExpired = expires < now;
        const daysLeft = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));
        
        return (
          <div className={isExpired ? 'text-red-600' : 'text-green-600'}>
            <div className="text-xs">
              {isExpired ? '❌ Expired' : `✅ ${daysLeft} days left`}
            </div>
            <div className="text-xs text-gray-500">{expires.toLocaleDateString()}</div>
          </div>
        );
      }
    }
  ];

  return (
    <>
      <Table columns={columns} data={clicks} />
      {totalPages > 1 && (
        <div className="mt-6">
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={onPageChange} />
        </div>
      )}
    </>
  );
}

// Conversions Tab Component
function ConversionsTab({ data, loading, page, onPageChange }) {
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  const conversions = data?.conversions || [];
  const totalPages = data?.totalPages || 1;

  if (conversions.length === 0) {
    return <div className="text-center text-gray-500 py-8">No conversions yet</div>;
  }

  const columns = [
    {
      key: 'converted_at',
      label: 'Date',
      render: (conversion) => new Date(conversion.converted_at).toLocaleString()
    },
    {
      key: 'user',
      label: 'User',
      render: (conversion) => (
        <div>
          <div className="font-medium">{conversion.user_name}</div>
          <div className="text-sm text-gray-500">{conversion.user_email}</div>
        </div>
      )
    },
    {
      key: 'tier',
      label: 'Current Tier',
      render: (conversion) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 capitalize">
          {conversion.user_tier}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (conversion) => (
        <Link
          to={`/accounts/${conversion.user_id}`}
          className="text-purple-600 hover:text-purple-900 text-sm font-medium"
        >
          View User
        </Link>
      )
    }
  ];

  return (
    <>
      <Table columns={columns} data={conversions} />
      {totalPages > 1 && (
        <div className="mt-6">
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={onPageChange} />
        </div>
      )}
    </>
  );
}

// Commissions Tab Component
function CommissionsTab({ data, loading, page, onPageChange }) {
  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  const commissions = data?.commissions || [];
  const totalPages = data?.totalPages || 1;

  if (commissions.length === 0) {
    return <div className="text-center text-gray-500 py-8">No commissions yet</div>;
  }

  const columns = [
    {
      key: 'triggered_at',
      label: 'Date',
      render: (commission) => new Date(commission.triggered_at).toLocaleString()
    },
    {
      key: 'user',
      label: 'User',
      render: (commission) => (
        <div>
          <div className="font-medium">{commission.user_name}</div>
          <div className="text-sm text-gray-500">{commission.user_email}</div>
        </div>
      )
    },
    {
      key: 'plan',
      label: 'Plan',
      render: (commission) => (
        <div>
          <div className="font-medium capitalize">{commission.plan_type}</div>
          <div className="text-sm text-gray-500 capitalize">{commission.billing_period}</div>
        </div>
      )
    },
    {
      key: 'amount',
      label: 'Amount',
      render: (commission) => (
        <span className="font-semibold text-purple-600">
          ${parseFloat(commission.amount).toFixed(2)}
        </span>
      )
    },
    {
      key: 'status',
      label: 'Status',
      render: (commission) => (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
          commission.status === 'paid'
            ? 'bg-green-100 text-green-800'
            : commission.status === 'approved'
            ? 'bg-blue-100 text-blue-800'
            : commission.status === 'pending'
            ? 'bg-yellow-100 text-yellow-800'
            : 'bg-gray-100 text-gray-800'
        }`}>
          {commission.status}
        </span>
      )
    }
  ];

  return (
    <>
      <Table columns={columns} data={commissions} />
      {totalPages > 1 && (
        <div className="mt-6">
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={onPageChange} />
        </div>
      )}
    </>
  );
}

// Discount Coupon Modal Component
function DiscountCouponModal({ affiliate, onClose, onSuccess }) {
  const [formData, setFormData] = useState({
    discountCoupon: affiliate.discount_coupon || '',
    discountType: affiliate.discount_type || 'percent',
    discountPercent: affiliate.discount_percent || '',
    discountAmount: affiliate.discount_amount || '',
    discountDescription: affiliate.discount_description || ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await axios.put(`${API_BASE_URL}/api/admin/affiliates/${affiliate.id}`, {
        discountCoupon: formData.discountCoupon || null,
        discountType: formData.discountType,
        discountPercent: formData.discountPercent ? parseFloat(formData.discountPercent) : 0,
        discountAmount: formData.discountAmount ? parseFloat(formData.discountAmount) : 0,
        discountDescription: formData.discountDescription || null
      });
      onSuccess();
    } catch (error) {
      console.error('Error updating discount settings:', error);
      setError(error.response?.data?.error || 'Failed to update discount settings');
      setLoading(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm('Are you sure you want to remove the discount coupon for this affiliate?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await axios.put(`${API_BASE_URL}/api/admin/affiliates/${affiliate.id}`, {
        discountCoupon: null,
        discountType: 'percent',
        discountPercent: 0,
        discountAmount: 0,
        discountDescription: null
      });
      onSuccess();
    } catch (error) {
      console.error('Error clearing discount settings:', error);
      setError(error.response?.data?.error || 'Failed to clear discount settings');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full">
        <div className="p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Discount Coupon Settings</h2>
          <p className="text-sm text-gray-500 mt-1">
            Configure a discount coupon that will be automatically applied when customers referred by this affiliate check out.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              iCount Coupon Code
            </label>
            <input
              type="text"
              value={formData.discountCoupon}
              onChange={(e) => setFormData(prev => ({ ...prev, discountCoupon: e.target.value }))}
              placeholder="Enter the coupon code from iCount"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-mono"
            />
            <p className="mt-1 text-xs text-gray-500">
              This should match the coupon code configured in your iCount system
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Discount Type
            </label>
            <div className="flex space-x-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="discountType"
                  value="percent"
                  checked={formData.discountType === 'percent'}
                  onChange={(e) => setFormData(prev => ({ ...prev, discountType: e.target.value }))}
                  className="form-radio text-purple-600 focus:ring-purple-500"
                />
                <span className="ml-2 text-gray-700">Percentage (%)</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="discountType"
                  value="fixed"
                  checked={formData.discountType === 'fixed'}
                  onChange={(e) => setFormData(prev => ({ ...prev, discountType: e.target.value }))}
                  className="form-radio text-purple-600 focus:ring-purple-500"
                />
                <span className="ml-2 text-gray-700">Fixed Amount ($)</span>
              </label>
            </div>
          </div>

          {formData.discountType === 'percent' ? (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Discount Percentage
              </label>
              <div className="relative">
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.discountPercent}
                  onChange={(e) => setFormData(prev => ({ ...prev, discountPercent: e.target.value }))}
                  placeholder="e.g., 10"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-8"
                />
                <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                For display purposes - the actual discount is managed in iCount
              </p>
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Discount Amount
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.discountAmount}
                  onChange={(e) => setFormData(prev => ({ ...prev, discountAmount: e.target.value }))}
                  placeholder="e.g., 25.00"
                  className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                For display purposes - the actual discount is managed in iCount
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description (optional)
            </label>
            <input
              type="text"
              value={formData.discountDescription}
              onChange={(e) => setFormData(prev => ({ ...prev, discountDescription: e.target.value }))}
              placeholder="e.g., 10% off first month for new customers"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          <div className="flex justify-between pt-4">
            <div>
              {affiliate.discount_coupon && (
                <button
                  type="button"
                  onClick={handleClear}
                  disabled={loading}
                  className="px-4 py-2 text-red-600 hover:text-red-700 text-sm font-medium disabled:opacity-50"
                >
                  Remove Coupon
                </button>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                disabled={loading}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AffiliateDetail;

